/**
 * Pagina publică de clip (Clip-Public.dc.html) — fără auth, doar vizionare, 1:1 cu
 * prototipul (temă întunecată fixă). 404 (token invalid/revocat) / 410 (expirat) elegant.
 * Footer viral „Făcut cu Reclame Video".
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getPublicClip } from '../api/endpoints';
import { ApiError } from '../api/client';
import { resolveMediaUrl } from '../lib/media';
import { formatDate } from '../lib/format';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'http://localhost:4603';

export function PublicClipPage() {
  const { token } = useParams<{ token: string }>();
  const q = useQuery({
    queryKey: ['public-clip', token],
    queryFn: () => getPublicClip(token!),
    enabled: Boolean(token),
    retry: false,
  });

  useEffect(() => {
    document.title = q.data?.clip.title ? `${q.data.clip.title} · Reclame Video` : 'Clip · Reclame Video';
  }, [q.data?.clip.title]);

  const dark = {
    bg: '#181411',
    ink: '#F3EEE6',
    muted: '#A99E90',
    accent: '#FF6A50',
    accentText: '#FF8E76',
    line: 'rgba(243,238,230,.14)',
    surface: '#252019',
  };

  const wrap: React.CSSProperties = {
    minHeight: '100vh',
    background: dark.bg,
    color: dark.ink,
    backgroundImage: 'radial-gradient(circle,rgba(243,238,230,.045) 1px,transparent 1.45px)',
    backgroundSize: '26px 26px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 16px',
    boxSizing: 'border-box',
    gap: 22,
    fontFamily: 'Manrope, system-ui, sans-serif',
  };

  if (q.isLoading) {
    return (
      <div style={wrap}>
        <span style={{ color: dark.muted, fontSize: 13 }}>Se încarcă clipul…</span>
      </div>
    );
  }

  if (q.isError) {
    const status = q.error instanceof ApiError ? q.error.status : 0;
    const gone = status === 410;
    return (
      <div style={wrap}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoDark accent={dark.accent} />
        </div>
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <p className="bricolage" style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>
            {gone ? 'Linkul a expirat' : 'Link indisponibil'}
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 13.5, color: dark.muted, lineHeight: 1.6 }}>
            {gone
              ? 'Perioada de vizionare a acestui clip s-a încheiat. Cere-i expeditorului un link nou.'
              : 'Clipul nu mai este disponibil sau linkul a fost dezactivat.'}
          </p>
        </div>
        <a href={SITE_URL} style={{ ...pillBtn(dark), textDecoration: 'none' }}>
          Făcut cu <span style={{ color: dark.accentText, fontFamily: "'Bricolage Grotesque',sans-serif" }}>Reclame Video</span> →
        </a>
      </div>
    );
  }

  const d = q.data!;
  const videoUrl = resolveMediaUrl(d.video.url);

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, animation: 'rv-in .35s both' }}>
        <LogoDark accent={dark.accent} />
        <span style={{ fontSize: 13, color: dark.muted }}>ți-a trimis un clip</span>
      </div>

      <div style={{ position: 'relative', width: 'min(320px,88vw)', animation: 'rv-in .4s .08s both' }}>
        <div style={{ position: 'relative', aspectRatio: d.clip.format.replace(':', '/'), borderRadius: 22, overflow: 'hidden', background: '#241C15', boxShadow: '0 30px 70px -30px rgba(0,0,0,.85)', border: `1px solid ${dark.line}` }}>
          {videoUrl ? (
            <video src={videoUrl} controls playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'rgba(255,255,255,.45)', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>
              [clip]
            </span>
          )}
        </div>
        <span className="tnum" style={{ position: 'absolute', top: -11, right: -8, background: dark.surface, border: `1px solid ${dark.line}`, borderRadius: 999, padding: '7px 12px', fontSize: 11.5, fontWeight: 800, color: dark.ink }}>
          {d.clip.format}
        </span>
      </div>

      <div style={{ textAlign: 'center', animation: 'rv-in .4s .16s both' }}>
        <p className="bricolage" style={{ margin: 0, fontWeight: 800, fontSize: 20 }}>{d.clip.title || 'Clip video'}</p>
        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: dark.muted }}>
          link valabil până pe {formatDate(d.expiresAt)} · doar vizionare
        </p>
      </div>

      <a href={d.branding.ctaUrl || SITE_URL} style={{ ...pillBtn(dark), textDecoration: 'none', animation: 'rv-in .4s .24s both' }}>
        {d.branding.poweredBy || 'Făcut cu Reclame Video'}{' '}
        <span style={{ color: dark.accentText, fontFamily: "'Bricolage Grotesque',sans-serif" }}>— clipuri AI în ~10 minute →</span>
      </a>
    </div>
  );
}

function pillBtn(dark: { line: string; muted: string }): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: `1px solid ${dark.line}`,
    borderRadius: 999,
    padding: '11px 18px',
    fontSize: 12.5,
    fontWeight: 800,
    color: dark.muted,
  };
}

function LogoDark({ accent }: { accent: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#F3EEE6', color: '#181411', padding: '7px 10px', borderRadius: 999, fontSize: 11, letterSpacing: '.14em', fontWeight: 800 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: accent, animation: 'rv-pulse 2.2s infinite' }} />
      REC
    </span>
  );
}
